// components/GlobalSearch/GlobalSearch.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Spinner, ListGroup } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import useDebounce from "../components/Debounce";
import { fetchGlobalSearchResults } from "../services/reactapis";
import "../App.css";
import SearchIcon from '../assets/search.webp';
import CustomDropdown from "../components/CustomDropdown";
import { menuItems } from "../Sidebar/MenuItems";

const CATEGORY_ORDER = ["Users", "Posts", "Gifts", "Withdrawals", "Hashtags" ,
  "Currencies" , "Coin Packages" , "Reports" , "Dummy Lives" ,  
];

function HighlightedText({ text, query }) {
  if (!text) return null;
  if (!query) return <>{text}</>;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <>{text}</>;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <mark className="gs-highlight">{match}</mark>
      {after}
    </>
  );
}

const SEARCH_CATEGORIES = [
  {
    label: "Users",
    icon: "bi bi-people",
    path: "/Users",
  },
  {
    label: "Posts",
    icon: "bi bi-layers",
    path: "/Posts",
  },
  {
    label: "Gifts",
    icon: "bi bi-gift",
    path: "/Gifts",
  },
  {
    label: "Withdrawals",
    icon: "bi bi-wallet-fill",
    path: "/Withdrawal",
  },
  {
    label: "Hashtags",
    icon: "bi bi-hash",
    path: "/Hashtags",
  },
  {
    label: "Currencies",
    icon: "bi bi-currency-exchange",
    path: "/currencygrid",
  },
  {
    label: "Coin Packages",
    icon: "bi bi-box-seam",
    path: "/coinpackages",
  },
  {
    label: "Reports",
    icon: "bi bi-exclamation-triangle",
    path: "/Reports",
  },
  {
    label: "Dummy Lives",
    icon: "bi bi-person-badge-fill",
    path: "/lives",
  },
];


export default function GlobalSearch({ autoFocus = false, onClose, narrowMode = false }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const cancelTokenRef = useRef(null);

  // Auto-focus when used in mobile search overlay
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);



  const [selectedCategory, setSelectedCategory] = useState("Select Category");
  const [showCategory, setShowCategory] = useState(false);
  // const [selectedCategory, setSelectedCategory] = useState(null);


  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fire the API call whenever the debounced query changes.
  // useEffect(() => {
  //   const trimmed = debouncedQuery.trim();

  //   if (!trimmed) {
  //     setResults({});
  //     setIsLoading(false);
  //     setIsOpen(false);
  //     return;
  //   }

  //   if (cancelTokenRef.current) {
  //     cancelTokenRef.current.cancel("New search initiated");
  //   }
  //   cancelTokenRef.current = axios.CancelToken.source();

  //   setIsLoading(true);
  //   setIsOpen(true);

  //   const validCategories = ["Users", "Posts", "Gifts", "Withdrawals", "Hashtags"];
  //   const apiCategory = validCategories.includes(selectedCategory) ? selectedCategory : "";

  //   fetchGlobalSearchResults(trimmed, cancelTokenRef.current.token)
  //     .then((response) => {
  //       setResults(response.data?.results || {});
  //     })
  //     .catch((error) => {
  //       if (!axios.isCancel(error)) {
  //         console.error("Global search failed:", error);
  //         setResults({});
  //       }
  //     })
  //     .finally(() => {
  //       setIsLoading(false);
  //     });

  //   return () => {
  //     if (cancelTokenRef.current) {
  //       cancelTokenRef.current.cancel("Component unmounted or query changed");
  //     }
  //   };
  // }, [debouncedQuery, selectedCategory]);

  useEffect(() => {
  const trimmed = debouncedQuery.trim();

  if (!trimmed) {
    setResults({});
    setIsLoading(false);
    setIsOpen(false);
    return;
  }

  if (cancelTokenRef.current) {
    cancelTokenRef.current.cancel("New search initiated");
  }
  cancelTokenRef.current = axios.CancelToken.source();

  setIsLoading(true);
  setIsOpen(true);

  const validCategories = ["Users", "Posts", "Gifts", "Withdrawals", "Hashtags" ,
   "Currencies" , "Coin Packages" , "Reports" , "Dummy Lives" 
];
  const apiCategory = validCategories.includes(selectedCategory) ? selectedCategory : "";

  fetchGlobalSearchResults(trimmed, cancelTokenRef.current.token, apiCategory) 
    .then((response) => {
      setResults(response.data?.results || {});
    })
    .catch((error) => {
      if (!axios.isCancel(error)) {
        console.error("Global search failed:", error);
        setResults({});
      }
    })
    .finally(() => {
      setIsLoading(false);
    });

  return () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Component unmounted or query changed");
    }
  };
}, [debouncedQuery, selectedCategory]);

  useEffect(() => {

    const current = menuItems.find(
      item => item.path === location.pathname
    );

    if (current) {
      setSelectedCategory(current.label);
    }

  }, [location.pathname]);

  const flattenedResults = CATEGORY_ORDER.filter((cat) => results[cat]?.length).flatMap(
    (cat) => results[cat].map((item) => ({ ...item, category: cat }))
  );


  const highlightTerm = debouncedQuery.startsWith("#")
    ? debouncedQuery.slice(1)
    : debouncedQuery;

  // const handleSelect = useCallback(
  //   (item) => {
  //     if (!item) return;

  //     setQuery("");
  //     setResults({});
  //     setIsOpen(false);
  //     setActiveIndex(-1);

  //     if (item.category === "Hashtags") {
  //       navigate(item.route, { state: { highlightTag: item.label.replace("#", "") } });
  //       return;
  //     }


  //       if (item.category === "Users") {
  //     const searchTerm = item.label;
  //     navigate(`${item.route}?search=${encodeURIComponent(searchTerm)}`);
  //     return;
  //   }

  //     navigate(item.route);
  //   },
  //   [navigate]
  // );







  //handleselect
  //   const handleSelect = useCallback(
  //   (item) => {
  //     if (!item) return;

  //     setQuery("");
  //     setResults({});
  //     setIsOpen(false);
  //     setActiveIndex(-1);

  //     if (item.category === "Hashtags") {
  //       navigate(item.route, { state: { highlightTag: item.label.replace("#", "") } });
  //       return;
  //     }

  //     if (item.category === "Users") {
  //       // Use the actual username from the item, not the original query
  //       const searchTerm = item.label; // This is the actual username
  //       navigate(`/Users?search=${encodeURIComponent(searchTerm)}`);
  //       return;
  //     }

  //     navigate(item.route);
  //   },
  //   [navigate]
  // );

  const getWithdrawalTab = (status) => {
    const normalizedStatus = String(status || "").toLowerCase().trim();

    if (normalizedStatus === "pending") {
        return "pending";
    }

    if (
        normalizedStatus === "approved" ||
        normalizedStatus === "paid"
    ) {
        return "completed";
    }

    if (normalizedStatus === "rejected") {
        return "rejected";
    }

    return "pending";
};

  const handleSelect = useCallback(

    
    (item) => {
      if (!item) return;

      

      setQuery("");
      setResults({});
      setIsOpen(false);
      setActiveIndex(-1);

      // if (item.category === "Hashtags") {
      //   navigate(`/Hashtags`, { state: { highlightTag: item.label.replace("#", "") } });
      //   return;
      // }

      if (item.category === "Hashtags") {
  navigate(`/Hashtags?search=${encodeURIComponent(item.label)}`);
  return;


  
}

// if (item.category === "Withdrawals") {
//     const getWithdrawalTab = (status) => {
//         if (status === "pending") return "pending";
//         if (status === "approved" || status === "paid") return "completed";
//         if (status === "rejected") return "rejected";

//         return "pending";
//     };

//     const tab = getWithdrawalTab(item.status);

//     navigate(
//         `/Withdrawal?search=${encodeURIComponent(item.label)}&tab=${tab}`
//     );

//     return;
// }

      // if (item.category === "Users") {
      //   navigate(`/Users?search=${encodeURIComponent(item.label)}`);
      //   return;
      // }
      // navigate(item.route);


      if (item.category === "Withdrawals") {
    const tab = getWithdrawalTab(item.status);

    console.log("Withdrawal item:", item);
    console.log("Status:", item.status);
    console.log("Selected tab:", tab);

    navigate(
        `/withdrawal?search=${encodeURIComponent(item.label)}&tab=${tab}`
    );

    return;
}

      const categoryItem = SEARCH_CATEGORIES.find((m) => m.label === item.category);
      const targetPath = categoryItem ? categoryItem.path : "/Users";

      const params = new URLSearchParams({ search: item.label });
      if (item.userId) params.append("userId", item.userId);
      if (item.id) params.append("id", item.id);

      navigate(`${targetPath}?${params.toString()}`);

    },
    [navigate]
  );


  const handleKeyDown = (e) => {
    if (!isOpen || flattenedResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flattenedResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? flattenedResults.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) handleSelect(flattenedResults[activeIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const hasQuery = debouncedQuery.trim().length > 0;
  const hasResults = flattenedResults.length > 0;

  return (
    <div className="gs-container" ref={containerRef}>

      <div className="bothsearches">
        {/* <div className="search-dropdown">
        <div className="dropdowntext">
          <h6 className="dropdownarea">Select category</h6>
          <div className="dropdownicon">
          <i class="bi bi-caret-down"></i>
          </div>
          </div>
      </div > */}

        <div className="search-dropdown"
          onClick={() => setShowCategory(!showCategory)}
        >
          <div className="dropdowntext">

            <span className="dropdownarea">
              {selectedCategory}
            </span>

            <i className="bi bi-chevron-down dropdownicon"></i>

          </div>
          {
            showCategory && (

              <div className="custom-dropdown-menu">

                {
                  SEARCH_CATEGORIES.map((item) => (
                    <div
                      key={item.path}
                      className="custom-dropdown-item"
                      onClick={() => {
                        setSelectedCategory(item.label);
                        setShowCategory(false);
                      }}

                    >

                      <i className={`${item.icon}`}></i>

                      <span>{item.label}</span>

                    </div>
                  ))
                }

              </div>

            )
          }
        </div>

        <div className={`search-container${narrowMode ? " gs-search-hidden" : ""}`}>
          <input
            ref={inputRef}
            type="search"
            placeholder="Search users, or #hashtag..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onFocus={() => hasQuery && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            aria-label="Global search"
            autoComplete="off"
          />
          <button
            className="search-button"
          // onClick={handleSearch}
          >
            <img
              src={SearchIcon}
              alt="Search"
            />
          </button>

          {isLoading && (
            <Spinner animation="border" size="sm" className="gs-spinner" role="status" />
          )}

          {isOpen && hasQuery && (
            <div className="gs-dropdown shadow-sm">
              {isLoading && (
                <div className="gs-status">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Searching...
                </div>
              )}

              {!isLoading && !hasResults && (
                <div className="gs-status text-muted">No Results Found</div>
              )}

              {!isLoading &&
                hasResults &&
                CATEGORY_ORDER.filter((cat) => results[cat]?.length).map((category) => (
                  <div key={category} className="gs-group">
                    <div className="gs-group-title">{category}</div>
                    <ListGroup variant="flush">
                      {results[category].map((item) => {
                        const flatIndex = flattenedResults.findIndex(
                          (f) => f.category === category && f.id === item.id
                        );
                        return (
                          <ListGroup.Item
                            key={`${category}-${item.id}`}
                            action
                            active={flatIndex === activeIndex}
                            onMouseDown={() => handleSelect({ ...item, category })}
                            onMouseEnter={() => setActiveIndex(flatIndex)}
                            className="gs-item"
                          >
                            {item.image && (
                              <img src={item.image} alt="" className="gs-item-thumb" />
                            )}
                            <div className="gs-item-text">
                              <div>
                                <HighlightedText text={item.label} query={highlightTerm} />
                              </div>
                              {item.subLabel && (
                                <div className="gs-item-sublabel">{item.subLabel}</div>
                              )}
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </div>
                ))}
            </div>
          )}

        </div>

      </div>


    </div>
  );
}
